import { memo, useState, useEffect, useRef } from 'preact/compat';
import { useAppSelector } from '../hooks';
import { useColumnResize } from '../hooks';
import { useCopyCell } from '../hooks';
import { useCopyRow } from '../hooks';
import { useVirtualScroll } from '../hooks/useVirtualScroll';
import { selectSelectedOffer } from '../store/selectors';
import ColumnVisibilityModal from './ColumnVisibilityModal';
import ColumnFilter from './ColumnFilter';
import LoadingOverlay from './LoadingOverlay';
import OfferSkeleton from './OfferSkeleton';
import type { IDealDetailsProps } from '../types';
import type { OfferRowField } from '../constants/columns';
import { COORDINATOR_FIELDS, NUMBER_FIELDS, FIELD_LABELS, INITIAL_WIDTHS } from '../constants/columns';
import { copyToClipboard } from '../utils/clipboard';
import GLightbox from 'glightbox';
import 'glightbox/dist/css/glightbox.min.css';

/** Format Oracle numeric strings: "1.00" → "1", "3599.50" → "3599.5" */
function formatCell(field: string, value: any): string {
    if (value == null) return '';
    const s = String(value);
    if (field === 'num_row' || NUMBER_FIELDS.includes(field as any)) {
        const n = Number(s);
        if (!isNaN(n)) return Number.isInteger(n) ? String(n) : n.toFixed(2);
    }
    return s;
}

const SuggestionTable = memo(({ isOpen }: IDealDetailsProps) => {
    const offer = useAppSelector(selectSelectedOffer);
    const rows = useAppSelector(s => s.offers.rows);
    const loadingRows = useAppSelector(s => s.offers.loadingRows);

    const { handleCellClick, getCellClass, clearSelection: clearCellSelection } = useCopyCell();
    const { handleRowClick, getRowClass, selectedRowId, clearSelection: clearRowSelection } = useCopyRow();
    const tableScrollRef = useRef<HTMLDivElement>(null);
    const scrollbarInnerRef = useRef<HTMLDivElement>(null);
    const widthDivRef = useRef<HTMLDivElement>(null);
    const [isColumnVisibilityModalOpen, setIsColumnVisibilityModalOpen] = useState(false);
    const [activeCell, setActiveCell] = useState<{ rowIndex: number; colIndex: number } | null>(null);
    const [draggedColumn, setDraggedColumn] = useState<string | null>(null);
    const [dragOverColumn, setDragOverColumn] = useState<string | null>(null);
    const [isResizing, setIsResizing] = useState(false);
    const [columnFilters, setColumnFilters] = useState<Record<string, Set<any>>>({});
    const [columnRanges, setColumnRanges] = useState<Record<string, { min: string; max: string }>>({});
    const [openFilterField, setOpenFilterField] = useState<OfferRowField | null>(null);
    const [sortConfig, setSortConfig] = useState<{ field: OfferRowField | null; direction: 'asc' | 'desc' }>({
        field: null,
        direction: 'asc',
    });

    const selectedCode = offer?.code ?? null;
    const storageKey = `visibleColumns_offer_${selectedCode}`;
    const columnOrderKey = `columnOrder_offer_${selectedCode}`;

    const [visibleColumns, setVisibleColumns] = useState<Set<string>>(() => {
        try {
            const saved = localStorage.getItem(storageKey);
            if (saved) return new Set(JSON.parse(saved));
        } catch {}
        return new Set(COORDINATOR_FIELDS as string[]);
    });

    const [columnOrder, setColumnOrder] = useState<OfferRowField[]>(() => {
        try {
            const saved = localStorage.getItem(columnOrderKey);
            if (saved) {
                const parsed: string[] = JSON.parse(saved);
                const validOrder = parsed.filter(col => COORDINATOR_FIELDS.includes(col as OfferRowField)) as OfferRowField[];
                const missing = COORDINATOR_FIELDS.filter(col => !validOrder.includes(col));
                return [...validOrder, ...missing];
            }
        } catch {}
        return [...COORDINATOR_FIELDS];
    });

    useEffect(() => {
        try { localStorage.setItem(storageKey, JSON.stringify(Array.from(visibleColumns))); } catch {}
    }, [visibleColumns, storageKey]);

    useEffect(() => {
        try { localStorage.setItem(columnOrderKey, JSON.stringify(columnOrder)); } catch {}
    }, [columnOrder, columnOrderKey]);

    useEffect(() => {
        const handleMouseUp = () => setIsResizing(false);
        if (isResizing) {
            document.addEventListener('mouseup', handleMouseUp);
            return () => document.removeEventListener('mouseup', handleMouseUp);
        }
    }, [isResizing]);

    // Sync horizontal scrollbar
    useEffect(() => {
        const tableScroll = tableScrollRef.current;
        const scrollbarInner = scrollbarInnerRef.current;
        if (!tableScroll || !scrollbarInner) return;

        let isScrollingTable = false;
        let isScrollingBar = false;

        const handleTableScroll = () => {
            if (!isScrollingBar) {
                isScrollingTable = true;
                scrollbarInner.scrollLeft = tableScroll.scrollLeft;
                isScrollingTable = false;
            }
        };
        const handleScrollbarScroll = () => {
            if (!isScrollingTable) {
                isScrollingBar = true;
                tableScroll.scrollLeft = scrollbarInner.scrollLeft;
                isScrollingBar = false;
            }
        };

        tableScroll.addEventListener('scroll', handleTableScroll);
        scrollbarInner.addEventListener('scroll', handleScrollbarScroll);

        const updateScrollbarWidth = () => {
            const table = tableScroll.querySelector('table') as HTMLElement;
            const widthDiv = widthDivRef.current;
            if (table && widthDiv) {
                widthDiv.style.minWidth = table.scrollWidth + 'px';
            }
        };

        updateScrollbarWidth();
        const resizeObserver = new ResizeObserver(updateScrollbarWidth);
        const table = tableScroll.querySelector('table');
        if (table) resizeObserver.observe(table);

        return () => {
            tableScroll.removeEventListener('scroll', handleTableScroll);
            scrollbarInner.removeEventListener('scroll', handleScrollbarScroll);
            resizeObserver.disconnect();
        };
    }, [rows.length, columnOrder.length, visibleColumns.size]);

    // GLightbox opened dynamically on click so it works with virtual scroll
    // (static selector binding misses rows rendered after scrolling).
    const lightboxRef = useRef<any>(null);
    useEffect(() => () => { if (lightboxRef.current) lightboxRef.current.destroy(); }, []);

    const filteredColumns = columnOrder.filter(col => visibleColumns.has(col));

    if (!isOpen) return null;
    if (!offer) return <OfferSkeleton />;

    // Filter rows
    const filteredItems = rows.filter(row => {
        return filteredColumns.every(field => {
            // Диапазон для числовых столбцов (≥ min, ≤ max)
            const range = columnRanges[field];
            if (range && (range.min !== '' || range.max !== '')) {
                const num = Number(row[field]);
                if (!Number.isFinite(num)) return false;
                if (range.min !== '' && num < Number(range.min)) return false;
                if (range.max !== '' && num > Number(range.max)) return false;
            }
            const filter = columnFilters[field];
            if (!filter || filter.size === 0) return true;
            const value = String(row[field] ?? '').trim();
            const displayValue = value === '' ? '(пусто)' : value;
            return filter.has(displayValue);
        });
    });

    // Sort rows
    const sortedItems = [...filteredItems].sort((a, b) => {
        if (!sortConfig.field) return 0;
        const field = sortConfig.field;
        const aValue = a[field];
        const bValue = b[field];
        const isNumber = NUMBER_FIELDS.includes(field);
        let comparison = 0;
        if (isNumber) {
            const aNum = typeof aValue === 'number' ? aValue : parseFloat(String(aValue ?? 0)) || 0;
            const bNum = typeof bValue === 'number' ? bValue : parseFloat(String(bValue ?? 0)) || 0;
            comparison = aNum - bNum;
        } else {
            const aStr = String(aValue ?? '').toLowerCase();
            const bStr = String(bValue ?? '').toLowerCase();
            comparison = aStr.localeCompare(bStr, 'ru');
        }
        return sortConfig.direction === 'asc' ? comparison : -comparison;
    });

    // Open photo lightbox dynamically — gallery built from the currently filtered rows
    const openPhoto = (url: string) => {
        const elements: any[] = sortedItems
            .filter(r => r.foto_url)
            .map(r => ({ href: r.foto_url, type: 'image' }));
        const startAt = Math.max(0, elements.findIndex(e => e.href === url));
        if (lightboxRef.current) lightboxRef.current.destroy();
        lightboxRef.current = GLightbox({ elements, loop: true, startAt } as any);
        lightboxRef.current.open();
    };

    const handleFilterChange = (field: string, filterValues: Set<any>) => {
        setColumnFilters(prev => ({ ...prev, [field]: filterValues }));
    };
    const handleRangeChange = (field: string, range: { min: string; max: string }) => {
        setColumnRanges(prev => ({ ...prev, [field]: range }));
    };

    const handleSortChange = (field: string, direction: 'asc' | 'desc') => {
        setSortConfig({ field: field as OfferRowField, direction });
    };

    const { headerProps, resizeHandleProps } = useColumnResize(
        INITIAL_WIDTHS,
        `columnWidths_offer_${selectedCode}`
    );

    // Cell keyboard navigation
    const handleCellKeyDown = (e: KeyboardEvent, rowIndex: number, colIndex: number) => {
        const totalRows = sortedItems.length;
        const totalCols = filteredColumns.length;
        let newRowIndex = rowIndex;
        let newColIndex = colIndex;

        switch (e.key) {
            case 'ArrowUp':    e.preventDefault(); if (rowIndex > 0) newRowIndex = rowIndex - 1; break;
            case 'ArrowDown':  e.preventDefault(); if (rowIndex < totalRows - 1) newRowIndex = rowIndex + 1; break;
            case 'ArrowLeft':  e.preventDefault(); if (colIndex > 0) newColIndex = colIndex - 1; break;
            case 'ArrowRight': e.preventDefault(); if (colIndex < totalCols - 1) newColIndex = colIndex + 1; break;
            case 'Enter': e.preventDefault(); (e.currentTarget as HTMLElement)?.blur(); return;
            default: return;
        }

        const nextCellId = `${filteredColumns[newColIndex]}-${sortedItems[newRowIndex].code}`;
        const nextCell = document.querySelector(`[data-cell-id="${nextCellId}"]`) as HTMLElement;
        if (nextCell) {
            setActiveCell({ rowIndex: newRowIndex, colIndex: newColIndex });
            const nextField = filteredColumns[newColIndex];
            const nextRow = sortedItems[newRowIndex];
            handleCellClick(nextCellId, String(nextRow[nextField] ?? ''));
            nextCell.focus();
            const range = document.createRange();
            range.selectNodeContents(nextCell);
            const selection = window.getSelection();
            selection?.removeAllRanges();
            selection?.addRange(range);
        }
    };

    // Column drag & drop
    const handleColumnDragStart = (e: DragEvent, column: OfferRowField) => {
        if (isResizing) { e.preventDefault(); return; }
        const target = e.target as HTMLElement;
        if (target.closest('[data-resize-handle]')) { e.preventDefault(); return; }
        setDraggedColumn(column);
        if (e.dataTransfer) { e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', column); }
        if (target instanceof HTMLElement) target.style.opacity = '0.5';
    };
    const handleColumnDragEnd = (e: DragEvent) => {
        setDraggedColumn(null); setDragOverColumn(null);
        if (e.target instanceof HTMLElement) e.target.style.opacity = '1';
    };
    const handleColumnDragOver = (e: DragEvent, column: OfferRowField) => {
        e.preventDefault();
        if (e.dataTransfer) e.dataTransfer.dropEffect = 'move';
        if (draggedColumn && draggedColumn !== column) setDragOverColumn(column);
    };
    const handleColumnDragLeave = () => setDragOverColumn(null);
    const handleColumnDrop = (e: DragEvent, targetColumn: OfferRowField) => {
        e.preventDefault();
        if (!draggedColumn || draggedColumn === targetColumn) { setDraggedColumn(null); setDragOverColumn(null); return; }
        const newOrder = [...columnOrder];
        const draggedIndex = newOrder.indexOf(draggedColumn as OfferRowField);
        const targetIndex = newOrder.indexOf(targetColumn);
        if (draggedIndex === -1 || targetIndex === -1) { setDraggedColumn(null); setDragOverColumn(null); return; }
        newOrder.splice(draggedIndex, 1);
        newOrder.splice(targetIndex, 0, draggedColumn as OfferRowField);
        setColumnOrder(newOrder);
        setDraggedColumn(null); setDragOverColumn(null);
    };

    const hasData = rows.length > 0;

    const ROW_HEIGHT = 36;
    const { containerRef: virtualRef, startIndex: vStart, endIndex: vEnd, totalHeight: vTotal, offsetY: vOffset } = useVirtualScroll({
        totalItems: sortedItems.length,
        itemHeight: ROW_HEIGHT,
        overscan: 15,
    });
    const visibleItems = sortedItems.slice(vStart, vEnd);

    return (
        <div className="bg-base-100 rounded-lg shadow-sm border border-base-300 p-6 relative">
            <LoadingOverlay visible={loadingRows} text="Загрузка строк..." />

            <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold">
                    {offer.offer_id || `Предложение #${offer.code}`}
                    <span className="text-sm text-base-content/60 ml-2">({sortedItems.length} строк)</span>
                </h2>
            </div>

            {!hasData ? (
                <div className="flex flex-col items-center justify-center py-16 text-center">
                    <svg className="w-16 h-16 text-base-content/30 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <p className="text-lg text-base-content/70 mb-2">Нет данных</p>
                    <p className="text-sm text-base-content/50">Перейдите во вкладку "Работа с файлом" для загрузки</p>
                </div>
            ) : (
                <div className="relative flex flex-col" style={{ maxHeight: 'calc(100vh - 300px)' }}>
                    <div
                        ref={(el: HTMLDivElement | null) => { (tableScrollRef as any).current = el; (virtualRef as any).current = el; }}
                        className="flex-1 overflow-x-auto overflow-y-auto"
                        style={{ maxHeight: 'calc(100vh - 300px)' }}
                    >
                        <table className="w-auto min-w-full border-collapse text-sm">
                            <thead>
                                <tr className="bg-base-200 text-base-content font-normal">
                                    <th className="py-2 px-1.5 border border-base-300 w-12 text-center">
                                        <button
                                            className="btn btn-ghost btn-sm p-1 h-auto min-h-0"
                                            onClick={() => setIsColumnVisibilityModalOpen(true)}
                                            title="Настройка колонок"
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-base-content/70 hover:text-base-content" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                            </svg>
                                        </button>
                                    </th>
                                    {filteredColumns.map(field => {
                                        const isDragging = draggedColumn === field;
                                        const isDragOver = dragOverColumn === field;
                                        return (
                                            <th
                                                key={field}
                                                {...headerProps(field)}
                                                className={`py-2 px-2 border border-base-300 relative cursor-move select-none font-normal ${isDragging ? 'opacity-50' : ''} ${isDragOver ? 'border-primary border-2 bg-primary/10' : ''}`}
                                                draggable={!isResizing}
                                                onDragStart={e => handleColumnDragStart(e, field)}
                                                onDragEnd={handleColumnDragEnd}
                                                onDragOver={e => handleColumnDragOver(e, field)}
                                                onDragLeave={handleColumnDragLeave}
                                                onDrop={e => handleColumnDrop(e, field)}
                                            >
                                                <div className="flex items-center gap-1.5 pr-3 pb-2">
                                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 text-base-content/40 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                                                        <path strokeLinecap="round" strokeLinejoin="round" d="M4 8h16M4 16h16" />
                                                    </svg>
                                                    <span className="text-xs">{FIELD_LABELS[field] || field}</span>
                                                    {sortConfig.field === field && (
                                                        <span className="text-xs text-primary">{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                                                    )}
                                                </div>
                                                <div className="absolute bottom-0 right-0" style={{ marginBottom: '2px', marginRight: '2px' }}>
                                                    <ColumnFilter
                                                        field={field}
                                                        values={rows.map(item => item[field])}
                                                        onFilterChange={handleFilterChange}
                                                        isOpen={openFilterField === field}
                                                        onToggle={() => setOpenFilterField(openFilterField === field ? null : field)}
                                                        sortConfig={sortConfig}
                                                        onSortChange={handleSortChange}
                                                        isNumberField={NUMBER_FIELDS.includes(field)}
                                                        range={columnRanges[field]}
                                                        onRangeChange={handleRangeChange}
                                                    />
                                                </div>
                                                <div
                                                    data-resize-handle
                                                    className="absolute right-0 top-0 h-full w-2 cursor-col-resize hover:bg-primary/20 z-10"
                                                    style={{ marginRight: '-4px' }}
                                                    onMouseDown={e => {
                                                        e.stopPropagation();
                                                        setIsResizing(true);
                                                        resizeHandleProps(field).onMouseDown(e as any);
                                                    }}
                                                />
                                            </th>
                                        );
                                    })}
                                </tr>
                            </thead>
                            <tbody>
                                {vOffset > 0 && <tr style={{ height: `${vOffset}px` }} />}
                                {visibleItems.map((row, i) => {
                                    const rowIndex = vStart + i;
                                    const rowId = `row-${row.code}`;
                                    const rowData: Record<string, string> = {};
                                    filteredColumns.forEach(field => { rowData[field] = String(row[field] ?? ''); });
                                    const isRowSelected = selectedRowId === rowId;

                                    return (
                                        <tr
                                            key={row.code}
                                            style={{ height: `${ROW_HEIGHT}px` }}
                                            className={`hover:bg-base-100 ${isRowSelected ? '' : 'border-t border-base-300'} ${getRowClass(rowId)}`}
                                        >
                                            <td
                                                className="py-2 px-1.5 border border-base-300 text-center cursor-pointer hover:bg-primary/10 transition-colors relative"
                                                onClick={() => { clearCellSelection(); setActiveCell(null); handleRowClick(rowId, rowData); }}
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" className={`h-3 w-3 inline-block transition-colors ${selectedRowId === rowId ? 'text-primary' : 'text-base-content/40'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                                                </svg>
                                                {selectedRowId === rowId && (
                                                    <button className="absolute left-full top-1/2 -translate-y-1/2 ml-1 px-1.5 py-0.5 text-[10px] bg-primary text-primary-content rounded whitespace-nowrap z-20 shadow"
                                                        onClick={e => {
                                                            e.stopPropagation();
                                                            copyToClipboard(Object.values(rowData).join('\t'));
                                                        }}>
                                                        Копировать
                                                    </button>
                                                )}
                                            </td>
                                            {filteredColumns.map((field, colIndex) => {
                                                const cellId = `${field}-${row.code}`;
                                                const isActive = activeCell?.rowIndex === rowIndex && activeCell?.colIndex === colIndex;
                                                const cellValue = formatCell(field, row[field]);

                                                return (
                                                    <td
                                                        key={field}
                                                        className={`${field === 'foto_url' ? 'py-0' : 'py-2'} px-2 border border-base-300 font-normal whitespace-nowrap overflow-hidden ${getCellClass(cellId)} ${isActive ? 'ring-2 ring-primary' : ''}`}
                                                        style={{ minWidth: `${headerProps(field).style.minWidth}` }}
                                                        data-cell-id={cellId}
                                                        data-row-index={rowIndex}
                                                        data-col-index={colIndex}
                                                        onKeyDown={e => handleCellKeyDown(e, rowIndex, colIndex)}
                                                        onClick={e => {
                                                            e.stopPropagation();
                                                            clearRowSelection();
                                                            setActiveCell({ rowIndex, colIndex });
                                                            handleCellClick(cellId, cellValue);
                                                        }}
                                                        onFocus={() => setActiveCell({ rowIndex, colIndex })}
                                                    >
                                                        {field === 'foto_url' && cellValue ? (
                                                            <img src={cellValue} alt="" className="h-8 w-8 object-cover rounded cursor-pointer hover:opacity-80 block" loading="lazy"
                                                                onClick={e => { e.stopPropagation(); openPhoto(cellValue); }} />
                                                        ) : cellValue}
                                                    </td>
                                                );
                                            })}
                                        </tr>
                                    );
                                })}
                                {(vTotal - vOffset - visibleItems.length * ROW_HEIGHT) > 0 && (
                                    <tr style={{ height: `${vTotal - vOffset - visibleItems.length * ROW_HEIGHT}px` }} />
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            <ColumnVisibilityModal
                isOpen={isColumnVisibilityModalOpen}
                onClose={() => setIsColumnVisibilityModalOpen(false)}
                columns={COORDINATOR_FIELDS as string[]}
                columnLabels={FIELD_LABELS as Record<string, string>}
                visibleColumns={Array.from(visibleColumns)}
                onToggleColumn={(newVisibleColumns) => {
                    setVisibleColumns(newVisibleColumns);
                    const newOrder = columnOrder.filter(col => newVisibleColumns.has(col));
                    const hiddenColumns = Array.from(newVisibleColumns).filter(col => !newOrder.includes(col as OfferRowField));
                    setColumnOrder([...newOrder, ...hiddenColumns as OfferRowField[]]);
                }}
            />
        </div>
    );
});

export default SuggestionTable;
